import { useEffect, useMemo, useState } from 'react'
import { loadUserById } from '@domain/features/user/user-feature'

function fallbackDisplayName(userId: string | undefined): string {
  if (!userId) {
    return 'Unknown user'
  }
  return `User ${userId.slice(0, 6)}`
}

function normalizeName(value: string | undefined): string | undefined {
  const normalizedValue = value?.trim()
  if (!normalizedValue) {
    return undefined
  }
  return normalizedValue
}

export function useUserDisplayNames(userIds: Array<string | undefined>) {
  const [displayNamesById, setDisplayNamesById] = useState<Record<string, string>>({})

  const normalizedIds = useMemo(() => {
    const ids = new Set<string>()
    userIds.forEach((userId) => {
      const normalizedId = userId?.trim()
      if (normalizedId) {
        ids.add(normalizedId)
      }
    })
    return Array.from(ids)
  }, [userIds])

  useEffect(() => {
    const unresolvedIds = normalizedIds.filter((userId) => !(userId in displayNamesById))
    if (unresolvedIds.length === 0) {
      return
    }

    let cancelled = false

    const loadDisplayNames = async () => {
      const loadedDisplayNames = await Promise.all(
        unresolvedIds.map(async (userId) => {
          try {
            const userProfile = await loadUserById(userId)
            return [
              userId,
              normalizeName(userProfile.fullName)
                ?? normalizeName(userProfile.profile_slug)
                ?? fallbackDisplayName(userId),
            ] as const
          } catch {
            return [userId, fallbackDisplayName(userId)] as const
          }
        }),
      )

      if (cancelled) {
        return
      }

      setDisplayNamesById((previousMap) => {
        const nextMap = { ...previousMap }
        loadedDisplayNames.forEach(([userId, displayName]) => {
          nextMap[userId] = displayName
        })
        return nextMap
      })
    }

    void loadDisplayNames()

    return () => {
      cancelled = true
    }
  }, [displayNamesById, normalizedIds])

  return {
    resolveDisplayName: (userId: string | undefined): string => {
      if (!userId) {
        return 'Unknown user'
      }
      return displayNamesById[userId] ?? fallbackDisplayName(userId)
    },
  }
}
